// Quick-links bar (collection pills and blog topics): the indicator under the row shows how much of the row is
// visible and where it's scrolled to, the current link starts centred in view, and from 750px previous/next arrows
// appear at whichever end has more to scroll (with a fade on that edge), so it's clear the row scrolls.
const smooth = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
const arrowPath = {
  prev: 'M228,128a12,12,0,0,1-12,12H69l51.52,51.51a12,12,0,0,1-17,17l-72-72a12,12,0,0,1,0-17l72-72a12,12,0,0,1,17,17L69,116H216A12,12,0,0,1,228,128Z',
  next: 'M224.49,136.49l-72,72a12,12,0,0,1-17-17L187,140H40a12,12,0,0,1,0-24H187L135.51,64.48a12,12,0,0,1,17-17l72,72A12,12,0,0,1,224.49,136.49Z',
};

class LinkBar extends HTMLElement {
  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.abortController?.abort();
  }

  connectedCallback() {
    this.scroller = this.querySelector('[data-scroller]');
    if (!this.scroller) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.#addArrows(signal);

    const update = () => {
      const { scrollWidth, clientWidth, scrollLeft } = this.scroller;
      const max = scrollWidth - clientWidth;
      this.classList.toggle('is-scrollable', max > 1);
      this.classList.toggle('can-scroll-prev', scrollLeft > 1);
      this.classList.toggle('can-scroll-next', scrollLeft < max - 1);
      this.style.setProperty('--visible', String(Math.min(1, clientWidth / scrollWidth)));
      this.style.setProperty('--progress', String(max > 0 ? scrollLeft / max : 0));
      // The arrows sit on the row's middle line
      this.style.setProperty('--pl-bar-middle', `${this.scroller.offsetTop + this.scroller.offsetHeight / 2}px`);
    };
    this.scroller.addEventListener('scroll', update, { passive: true, signal });
    this.resizeObserver = new ResizeObserver(update);
    this.resizeObserver.observe(this.scroller);

    // The current link starts centred when it would otherwise be out of view
    const current = this.scroller.querySelector('[aria-current]');
    if (current && current.offsetLeft + current.offsetWidth > this.scroller.clientWidth) {
      this.scroller.scrollLeft = current.offsetLeft - (this.scroller.clientWidth - current.offsetWidth) / 2;
    }
    update();
  }

  #addArrows(signal) {
    if (this.querySelector('.pl-bar-arrow')) return;
    for (const direction of ['prev', 'next']) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `pl-bar-arrow pl-bar-arrow--${direction}`;
      button.setAttribute('aria-label', direction === 'prev' ? 'Scroll back' : 'Scroll for more');
      // A pointer convenience: keyboard and screen reader users move through the links, which scroll into view
      // themselves, so the arrows stay out of the tab order and the accessibility tree
      button.tabIndex = -1;
      button.setAttribute('aria-hidden', 'true');
      button.innerHTML = `<svg viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path fill="currentColor" d="${arrowPath[direction]}"/></svg>`;
      button.addEventListener(
        'click',
        () => {
          const step = this.scroller.clientWidth * 0.7;
          this.scroller.scrollBy({ left: direction === 'prev' ? -step : step, behavior: smooth() });
        },
        { signal }
      );
      this.append(button);
    }
  }
}

if (!customElements.get('pl-link-bar')) customElements.define('pl-link-bar', LinkBar);
